import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import { approvalRequestsApi } from '@/api/services/approvalRequests';
import {
  ApprovalRequestCategory,
  type ApprovalRequestRowDto,
} from '@/api/types/approvalRequests';
import { buildRequestColumns } from './columns';
import { ResolveDialog, type ResolveAction } from './ResolveDialog';

interface RequestsTabProps {
  category: ApprovalRequestCategory;
  canApprove: boolean;
  queryKey: readonly unknown[];
}

type ModalState =
  | { action: ResolveAction; mode: 'single'; id: string }
  | { action: ResolveAction; mode: 'bulk'; ids: string[] }
  | null;

export function RequestsTab({ category, canApprove, queryKey }: RequestsTabProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState<ModalState>(null);

  const query = useQuery({
    queryKey,
    queryFn: () => approvalRequestsApi.getForDataTable({ category, history: false }),
  });

  const rows = query.data?.items ?? [];

  const refresh = () => {
    setSelected(new Set());
    return queryClient.invalidateQueries({ queryKey });
  };

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () =>
    setSelected(allChecked ? new Set() : new Set(rows.map((r) => r.id)));

  const columns = useMemo<DataGridColumn<ApprovalRequestRowDto>[]>(() => {
    const dataCols = buildRequestColumns(category, false);
    if (!canApprove) return dataCols;

    const selectCol: DataGridColumn<ApprovalRequestRowDto> = {
      key: 'select',
      title: (
        <input
          type="checkbox"
          className="form-check-input"
          checked={allChecked}
          onChange={toggleAll}
          aria-label="Select all"
        />
      ),
      className: 'text-center',
      headerClassName: 'text-center',
      sortable: false,
      hideable: false,
      filterable: false,
      cell: (r) => (
        <input
          type="checkbox"
          className="form-check-input"
          checked={selected.has(r.id)}
          onChange={() => toggleOne(r.id)}
          aria-label="Select request"
        />
      ),
    };
    return [selectCol, ...dataCols];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, canApprove, allChecked, selected, rows]);

  const openBulk = (action: ResolveAction) => {
    const ids = [...selected];
    if (ids.length === 0) {
      toast.warning('Please check at least one request.');
      return;
    }
    setModal({ action, mode: 'bulk', ids });
  };

  const submitResolve = async (comment: string) => {
    if (!modal) return;
    try {
      if (modal.mode === 'single') {
        if (modal.action === 'approve') {
          await approvalRequestsApi.approve({ id: modal.id, comment });
        } else {
          await approvalRequestsApi.reject({ id: modal.id, comment });
        }
        toast.success('Request resolved.');
      } else {
        if (modal.action === 'approve') {
          await approvalRequestsApi.approveMany({ ids: modal.ids, comment });
        } else {
          await approvalRequestsApi.rejectMany({ ids: modal.ids, comment });
        }
        toast.success('Requests resolved.');
      }
      setModal(null);
      await refresh();
    } catch (err) {
      // Partial-failure messages (e.g. "{n} approved, {m} failed") come back verbatim;
      // still refetch since some may have succeeded, and keep the modal open.
      toast.error(err instanceof ApiError ? err.message : 'Failed to resolve the request.');
      await refresh();
    }
  };

  return (
    <>
      <DataGrid
        columns={columns}
        data={rows}
        rowKey={(r) => r.id}
        columnSearch
        includeExcel
        excelFileName="Requests"
        emptyMessage={query.isLoading ? 'Loading…' : 'No data.'}
        toolbarStart={
          <>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              title="Refresh"
              onClick={() => void refresh()}
            >
              <i className="fa fa-refresh" />
            </button>
            {canApprove && (
              <>
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openBulk('approve')}>
                  <i className="fa fa-check me-1" />Accept All
                </button>
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => openBulk('reject')}>
                  <i className="fa fa-times me-1" />Decline All
                </button>
              </>
            )}
          </>
        }
        rowActions={
          canApprove
            ? (row) => (
                <div className="d-flex gap-1">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    title="Approve"
                    onClick={() => setModal({ action: 'approve', mode: 'single', id: row.id })}
                  >
                    <i className="fa fa-check" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    title="Decline"
                    onClick={() => setModal({ action: 'reject', mode: 'single', id: row.id })}
                  >
                    <i className="fa fa-times" />
                  </button>
                </div>
              )
            : undefined
        }
      />
      {modal && (
        <ResolveDialog
          action={modal.action}
          count={modal.mode === 'bulk' ? modal.ids.length : 1}
          onClose={() => setModal(null)}
          onSubmit={submitResolve}
        />
      )}
    </>
  );
}
